import React, {
  useEffect, useMemo, useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import bbox from '@turf/bbox';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import nearestPoint from '@turf/nearest-point';
import distance from '@turf/distance';
import buffer from '@turf/buffer';
import intersect from '@turf/intersect';
import area from '@turf/area';
import { featureCollection, point } from '@turf/helpers';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import useMap from '../../../../hooks/useMap';

import Loader from '../../../Loader';
import MapHeader from '../MapHeader';
import MapInfo from '../MapInfo';

import { initIPFS } from '../../../../state/ipfs';
import {
  getStageMonitoringData, kilometeresToMeters, metersToKilometeres,
} from '../../../../utils/helpers';
import { MAP_EVENTS } from '../../../../constans/constans';
import RequestModal from '../../../RequestModal';

const SamplePlotsMap = ({ state, setState, setShow, currentStage, numberSampleZones, sampleZonesChanged, setSampleZonesChanged, sampleRadius, setSampleRadius, treeDensity, upperAttempCellSide,
  setUpperAttempCellSide }) => {
  const translate = useIntl();

  const { tools, mapRef, utils: { centerCoordinates, moveMap, zoomIn } } = useMap({ point: false, trash: false });
  const [newState, setNewState] = useState(structuredClone(state));
  const [cellSideAttempts, setCellSideAttempts] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const { targetSubZone, targetSampleZones } = useMemo(() => getStageMonitoringData(newState, currentStage), [newState]);
  const [isLoading, setIsLoading] = useState(false);
  // const history = useHistory();
  const sampleZonesLimit = useMemo(() => {
    const percentage = 0.1; // 10%
    const limit = Math.floor(numberSampleZones * percentage) || 1;
    return limit;
  }, []);
  const [changedZones, setChangedZones] = useState(structuredClone(sampleZonesChanged));
  const [warning, setWarning] = useState({ message: '', cause: '' });
  // Need for delayed MOUSE_MOVE event call
  let timer;
  const calcCellSide = (value, attempts = 0) => {
    const square = +value; // hectares
    const param = 10000;
    const param2 = 3;
    let cellSide;
    const attemptsVariant = upperAttempCellSide?.isDraw ? upperAttempCellSide.value : attempts;
    if (attemptsVariant === 0) {
      // Первая попытка - обычная логика
      cellSide = Math.max((2 * sampleRadius.value), Math.sqrt((square * param) / (param2 * numberSampleZones)));
    } else if (attemptsVariant === 1) {
      // Вторая попытка - 4 * sampleRadius.value
      cellSide = 4 * sampleRadius.value;
    } else if (attemptsVariant === 2) {
      // Третья попытка - 2 * sampleRadius.value
      cellSide = 2 * sampleRadius.value;
    } else {
      // Больше попыток - обычная логика
      cellSide = Math.max((2 * sampleRadius.value), Math.sqrt((square * param) / (param2 * numberSampleZones)));
    }

    return cellSide;
  };
  const metersToDegrees = (meters) => meters / 111320;
  // this func have previos logic
  // const createPointGrid = (mask, _count, lastNumber) => {
  //   const { polygon, subId, square } = mask;
  //   const cellSide = calcCellSide(square); // фиксированная дистанция
  //   console.log(cellSide, 'fixed cellSide');
  //   const [minX, minY, maxX, maxY] = bbox(polygon[0]);
  //
  //   const bufferRadius = cellSide / 2;
  //   const bufferDeg = metersToDegrees(bufferRadius);
  //
  //   let bestPoints = [];
  //   let bestBuffers = [];
  //
  //   let spacingFactor = 0.9;
  //   while (spacingFactor <= 1.5) {
  //     // eslint-disable-next-line no-loop-func
  //     const generateCandidates = (offsetX = 0, offsetY = 0) => {
  //       const candidates = [];
  //       const cols = Math.floor((maxX - minX) / (spacingFactor * bufferDeg));
  //       const rows = Math.floor((maxY - minY) / (spacingFactor * bufferDeg));
  //       for (let i = 0; i <= cols; i++) {
  //         for (let j = 0; j <= rows; j++) {
  //           const x = minX + i * (spacingFactor * bufferDeg) + offsetX;
  //           const y = minY + j * (spacingFactor * bufferDeg) + offsetY;
  //           candidates.push(point([x, y]));
  //         }
  //       }
  //       return candidates;
  //     };
  //
  //     // Сначала обычная сетка
  //     let candidates = generateCandidates(0, 0);
  //     candidates = candidates.sort(() => Math.random() - 0.5);
  //
  //     const validPoints = [];
  //     for (const pt of candidates) {
  //       const sampleBuffer = buffer(pt, metersToKilometeres(bufferRadius), { units: 'kilometers' });
  //       const sampleBufferCenterInside = booleanPointInPolygon(pt, polygon[0]);
  //       const bufferIntersection = intersect(sampleBuffer, polygon[0]);
  //       const bufferArea = area(sampleBuffer);
  //       const intersectionArea = bufferIntersection ? area(bufferIntersection) : 0;
  //       const bufferFullyInside = bufferIntersection && intersectionArea >= bufferArea * 0.999999;
  //
  //       if (sampleBufferCenterInside && bufferFullyInside) {
  //         validPoints.push({ pt, sampleBuffer });
  //       }
  //     }
  //
  //     const resultPoints = [];
  //     const debugBuffers = [];
  //     let index = 0;
  //
  //     while (validPoints.length && resultPoints.length < _count) {
  //       const { pt, sampleBuffer } = validPoints.shift();
  //
  //       let isFarEnough = true;
  //       for (const existing of resultPoints) {
  //         const d = distance(existing, pt, { units: 'kilometers' }) * 1000;
  //         if (d < cellSide * 0.999999) {
  //           isFarEnough = false;
  //           break;
  //         }
  //       }
  //
  //       if (isFarEnough) {
  //         const sampleName = `S${lastNumber + index + 1}`;
  //         resultPoints.push({
  //           ...pt,
  //           id: `${Date.now()}-${index}`,
  //           subId,
  //           coordinates: pt.geometry.coordinates,
  //           sampleName,
  //           properties: {
  //             ...pt.properties,
  //             sampleName,
  //           },
  //           sampleTrees: {
  //             historySampleTrees: [],
  //             currentSampleTrees: '',
  //           },
  //         });
  //
  //         debugBuffers.push({
  //           ...sampleBuffer,
  //           properties: {
  //             ...sampleBuffer.properties,
  //             user_color: 'red',
  //             debug: true,
  //           },
  //           id: `debug-buffer-${index}`,
  //         });
  //
  //         index++;
  //       }
  //     }
  //
  //     if (resultPoints.length >= bestPoints.length) {
  //       bestPoints = resultPoints;
  //       bestBuffers = debugBuffers;
  //     }
  //
  //     if (bestPoints.length >= _count) break;
  //
  //     // 🔥 Если точек не хватает — пробуем сдвинутую сетку
  //     const offsetCandidates = generateCandidates(bufferDeg / 2, bufferDeg / 2);
  //     const shuffledOffset = offsetCandidates.sort(() => Math.random() - 0.5);
  //
  //     const offsetValidPoints = [];
  //     for (const pt of shuffledOffset) {
  //       const sampleBuffer = buffer(pt, metersToKilometeres(bufferRadius), { units: 'kilometers' });
  //       const sampleBufferCenterInside = booleanPointInPolygon(pt, polygon[0]);
  //       const bufferIntersection = intersect(sampleBuffer, polygon[0]);
  //       const bufferArea = area(sampleBuffer);
  //       const intersectionArea = bufferIntersection ? area(bufferIntersection) : 0;
  //       const bufferFullyInside = bufferIntersection && intersectionArea >= bufferArea * 0.999999;
  //
  //       if (sampleBufferCenterInside && bufferFullyInside) {
  //         offsetValidPoints.push({ pt, sampleBuffer });
  //       }
  //     }
  //
  //     while (offsetValidPoints.length && bestPoints.length < _count) {
  //       const { pt, sampleBuffer } = offsetValidPoints.shift();
  //
  //       let isFarEnough = true;
  //       for (const existing of bestPoints) {
  //         const d = distance(existing, pt, { units: 'kilometers' }) * 1000;
  //         if (d < cellSide * 0.999) {
  //           isFarEnough = false;
  //           break;
  //         }
  //       }
  //
  //       if (isFarEnough) {
  //         const sampleName = `S${lastNumber + bestPoints.length + 1}`;
  //         bestPoints.push({
  //           ...pt,
  //           id: `${Date.now()}-${bestPoints.length}`,
  //           subId,
  //           coordinates: pt.geometry.coordinates,
  //           sampleName,
  //           properties: {
  //             ...pt.properties,
  //             sampleName,
  //           },
  //           sampleTrees: {
  //             historySampleTrees: [],
  //             currentSampleTrees: '',
  //           },
  //         });
  //
  //         bestBuffers.push({
  //           ...sampleBuffer,
  //           properties: {
  //             ...sampleBuffer.properties,
  //             user_color: 'red',
  //             debug: true,
  //           },
  //           id: `debug-buffer-${bestPoints.length}`,
  //         });
  //       }
  //     }
  //
  //     spacingFactor += 0.05;
  //   }
  //
  //   if (tools?.draw?.add && bestPoints.length) {
  //     tools.draw.add(featureCollection(bestBuffers));
  //   }
  //
  //   const limitedPoints = bestPoints.slice(0, _count);
  //   console.log(`✅ active placed samplePlots: ${limitedPoints.length} of ${_count} requested`);
  //   if (limitedPoints.length < _count) {
  //     console.warn(`⚠️ Warning: Only ${limitedPoints.length} of ${_count} points could be placed.`);
  //   }
  //   return limitedPoints;
  // };
  const createPointGrid = async (mask, _count, lastNumber, amountAttempt = 400, currentAttempts = 0) => {
    const { polygon, subId } = mask;
    const cellSide = calcCellSide(targetSubZone?.square, currentAttempts);
    const [minX, minY, maxX, maxY] = bbox(polygon[0]);
    const bufferRadius = cellSide / 2;
    const bufferDeg = metersToDegrees(bufferRadius);
    const generateCandidates = (offsetX = 0, offsetY = 0) => {
      const candidates = [];
      const cols = Math.floor((maxX - minX) / (bufferDeg));
      const rows = Math.floor((maxY - minY) / (bufferDeg));
      for (let i = 0; i <= cols; i++) {
        for (let j = 0; j <= rows; j++) {
          const x = minX + i * (bufferDeg) + offsetX;
          const y = minY + j * (bufferDeg) + offsetY;
          candidates.push(point([x, y]));
        }
      }
      return candidates;
    };

    let bestPoints = [];
    let usedAttempts = 0;
    for (let attempt = 0; attempt < amountAttempt; attempt++) {
      // eslint-disable-next-line no-unused-vars
      usedAttempts++;
      if (attempt % 50 === 0) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 10)); // 🔁 give a chance to render
      }
      const offsetX = (Math.random() - 0.5) * bufferDeg;
      const offsetY = (Math.random() - 0.5) * bufferDeg;
      const candidates = generateCandidates(offsetX, offsetY).sort(() => Math.random() - 0.5);
      const validPoints = [];

      for (const pt of candidates) {
        const sampleBuffer = buffer(pt, metersToKilometeres(bufferRadius), { units: 'kilometers' });
        const centerInside = booleanPointInPolygon(pt, polygon[0]);
        const bufferIntersection = intersect(sampleBuffer, polygon[0]);
        const bufferArea = area(sampleBuffer);
        const intersectionArea = bufferIntersection ? area(bufferIntersection) : 0;
        const fullyInside = bufferIntersection && intersectionArea >= bufferArea * 0.999999;
        if (centerInside && fullyInside) {
          validPoints.push({ pt, sampleBuffer });
        }
      }

      const attemptPoints = [];
      let index = 0;

      for (const { pt } of validPoints) {
        let isFarEnough = true;
        for (const existing of attemptPoints) {
          const d = distance(existing, pt, { units: 'kilometers' }) * 1000;
          if (d < cellSide * 0.999999) {
            isFarEnough = false;
            break;
          }
        }

        if (isFarEnough) {
          const sampleName = `S${lastNumber + index + 1}`;
          attemptPoints.push({
            ...pt,
            id: `${Date.now()}-${index}`,
            subId,
            coordinates: pt.geometry.coordinates,
            sampleName,
            properties: {
              ...pt.properties,
              sampleName,
            },
            sampleTrees: {
              historySampleTrees: [],
              currentSampleTrees: '',
            },
          });

          index++;
          if (attemptPoints.length >= _count) break;
        }
      }

      if (attemptPoints.length > bestPoints.length) {
        bestPoints = attemptPoints;
      }
      if (bestPoints.length >= _count) break;
    }
    const limitedPoints = bestPoints.slice(0, _count);
    console.log(`✅ active placed samplePlots: ${limitedPoints.length} of ${_count} requested after ${usedAttempts} attempts`);
    // Если удалось разместить все точки, сбрасываем счетчик попыток
    if (limitedPoints.length >= _count) {
      // setCellSideAttempts(0);
    }

    if (limitedPoints.length < _count) {
      console.log(limitedPoints.length, 'limitedPoints.length');
      const newAttempts = currentAttempts + 1;
      setCellSideAttempts(newAttempts);

      console.log(`attempts calcCellSide: ${newAttempts}`);

      if (newAttempts === 1) {
        // eslint-disable-next-line no-return-await
        return await createPointGrid(mask, _count, lastNumber, 200, newAttempts);
      } if (newAttempts === 2) {
        // eslint-disable-next-line no-return-await
        return await createPointGrid(mask, _count, lastNumber, 200, newAttempts);
      } if (newAttempts >= 3) {
        setUpperAttempCellSide({ value: 0, isDraw: false });
        setShowModal(true);
        console.log('error not enough attempts  calcCellSide');
      }

      console.warn(`⚠️ Warning: Only ${limitedPoints.length} of ${_count} points could be placed after ${usedAttempts} attempts.`);
    }
    return limitedPoints;
  };
  const getNearestPoint = (activePoint, points) => {
    // Remove active point from array to compare it with the rest
    const restPoints = points.filter((p) => p.id !== activePoint.id);
    const pointsCollection = featureCollection(restPoints);
    return nearestPoint(activePoint, pointsCollection);
  };

  const getPointsDistance = (from, to) => {
    const d = distance(from, to, { units: 'kilometers' });
    return kilometeresToMeters(d);
  };

  const redrawCurrentZoneFeatures = (data) => {
    const lastSubZone = data.subZones.slice(-1)[0];
    const sampleZones = data.sampleZones.filter((item) => item.subId === lastSubZone.subId);
    const allFeaturesCollection = featureCollection([
      lastSubZone.polygon[0],
      ...sampleZones,
    ]);
    tools.draw.add(structuredClone(allFeaturesCollection));
  };

  const isEditableFeature = (feature, subZone) => {
    // Disable the subzone editing and moving sample plots out of its zone
    if (feature.id === subZone.subId || !booleanPointInPolygon(feature, subZone.polygon[0])) {
      return false;
    }
    // Enable sample plots update if limit isn't reached
    if (changedZones.length < sampleZonesLimit) return true;

    // Enable sample plots update if they are editable
    const isChangedZone = changedZones.find((pointEl) => feature.id === pointEl?.id);

    if (isChangedZone) return true;

    return false;
  };

  const isValidDistanceBetweenPoints = (feature) => {
    const closestPoint = getNearestPoint(feature, targetSampleZones);
    const closestPointDistance = getPointsDistance(feature, closestPoint); // metres
    const minimalDistance = calcCellSide(targetSubZone.square, cellSideAttempts); // metres

    if (closestPointDistance < minimalDistance) return false;
    return true;
  };

  const getTargetFeature = (e) => {
    const eventFeatures = tools.map.queryRenderedFeatures(e.point);
    if (!eventFeatures?.length) return null;

    const [{ properties, geometry }] = eventFeatures;
    const eventFeaureId = properties.id || properties.parent;

    const pointGen = targetSampleZones.find((el) => el.id === eventFeaureId);
    const polygon = targetSubZone.polygon[0].id === eventFeaureId;

    if (pointGen) {
      return {
        ...pointGen,
        coordinates: [...geometry.coordinates],
        geometry: { ...geometry },
      };
    }
    if (polygon) return targetSubZone.polygon[0];

    return null;
  };

  const updateArea = (e) => {
    const features = getTargetFeature(e);
    if (!features || !isEditableFeature(features, targetSubZone)) {
      redrawCurrentZoneFeatures(newState);
      return;
    }

    const isValidDistance = isValidDistanceBetweenPoints(features);

    // check if point buffer is in zone
    const pointBuffer = buffer(features, metersToKilometeres(calcCellSide(targetSubZone.square, cellSideAttempts) / 2), { units: 'kilometers' });
    const intersection = intersect(pointBuffer, targetSubZone.polygon[0]);
    const bufferArea = area(pointBuffer);
    const intersectionArea = intersection ? area(intersection) : 0;
    const isBufferInside = intersectionArea / bufferArea > 0.999;

    if (!isBufferInside) {
      redrawCurrentZoneFeatures(newState);
      return;
    }

    const updSampleZones = [...newState.sampleZones];
    const targetSampleIndex = updSampleZones.findIndex((item) => item.id === features.id);
    updSampleZones.splice(targetSampleIndex, 1, {
      ...features,
      subId: targetSubZone.subId,
      sampleName: tools.draw.get(features.id).properties.sampleName,
      coordinates: features.geometry.coordinates,
      sampleTrees: {
        historySampleTrees: [],
        currentSampleTrees: '',
      },
    });

    setChangedZones((prev) => {
      const targetIndex = prev.findIndex((item) => item.id === features.id);
      if (targetIndex >= 0) return prev;
      prev.push({ id: features.id });
      return prev;
    });

    setNewState({ ...newState, sampleZones: updSampleZones });

    setWarning({
      message: isValidDistance ? '' : translate.formatMessage({ id: 'wizardForm.step2.map.smallDistance' }),
      cause: isValidDistance ? '' : 'sample-plot-distance',
    });
  };
  function removeSamplePoints(original, toRemove) {
    const removeIds = new Set(toRemove.map((item) => item.subId));
    return original.filter((item) => !removeIds.has(item.subId));
  }
  const drawDefaultDataSampleZones = async () => {
    if (!targetSubZone) return;
    zoomIn(targetSubZone.polygon[0]);
    if (!targetSampleZones?.length) {
      const points = await createPointGrid(targetSubZone, numberSampleZones, newState.sampleZones.length, 400, cellSideAttempts);
      const updSampleZones = [...newState.sampleZones, ...points];
      const updState = { ...newState, sampleZones: updSampleZones };
      setNewState(updState);
      redrawCurrentZoneFeatures(updState);
      return;
    }
    if (!(sampleRadius.value === sampleRadius.savedValue)) {
      const cleanedArray = removeSamplePoints(newState.sampleZones, targetSampleZones);
      const points = await createPointGrid(targetSubZone, numberSampleZones, cleanedArray.length, 400, 0);
      const updSampleZones = [...cleanedArray, ...points];
      const updState = { ...newState, sampleZones: updSampleZones };
      setNewState(updState);
      redrawCurrentZoneFeatures(updState);
      return;
    }

    redrawCurrentZoneFeatures(newState);
    setNewState(newState);
  };
  const withLoader = async (func) => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      await func();
    } finally {
      setIsLoading(false);
    }
  };

  const disableWheelClick = (e) => {
    const isWheelClicked = e.originalEvent.which === 2;
    if (isWheelClicked) redrawCurrentZoneFeatures(newState);
  };

  const disableRightClick = () => redrawCurrentZoneFeatures(newState);

  // Delayed MOUSE_MOVE event handler
  const moveFeatures = (e) => {
    // If left mouse button is pressed
    if (e.originalEvent.which === 1) {
      clearTimeout(timer);
      timer = setTimeout(() => updateArea(e), 200);
    }
  };

  const switchMapEventListeners = (method) => {
    tools.map[method](MAP_EVENTS.RIGHT_CLICK, disableRightClick);
    tools.map[method](MAP_EVENTS.MOUSE_DOWN, disableWheelClick);
    tools.map[method](MAP_EVENTS.MOVE, moveMap);
    // draw.update event from mapbox-gl-draw doesn't work correctly (problems with external mouse and touchpad)
    tools.map[method](MAP_EVENTS.MOUSE_MOVE, moveFeatures);
  };

  const saveScreenshot = (cid) => {
    const updSubZone = [...newState.subZones];
    const targetZone = updSubZone.pop();
    targetZone.cidSampleScreenShot = `/ipfs/${cid}`;
    updSubZone.push(targetZone);
    setState({ ...newState, subZones: updSubZone });
  };

  const saveData = () => {
    const canvas = tools.map.getCanvas();
    setUpperAttempCellSide({ value: cellSideAttempts, isDraw: true });

    canvas.toBlob(async (blob) => {
      try {
        const ipfs = await initIPFS();
        const res = await ipfs.add(blob);
        if (!res) throw new Error();
        saveScreenshot(res.path);
        setSampleZonesChanged(changedZones);
        setIsLoading(false);
        setShow(false);
        setSampleRadius({ ...sampleRadius, savedValue: sampleRadius.value, savedTreeDensity: treeDensity });
      } catch (e) {
        console.log(e);
      }
    });
  };

  const scaleBeforeSave = () => {
    setIsLoading(true);
    zoomIn(targetSubZone.polygon[0]);

    if (tools.map.isZooming()) {
      tools.map.on(MAP_EVENTS.ZOOM_END, saveData);
    } else saveData();
  };

  const shufflePoints = async () => {
    const currentPoints = newState.sampleZones.filter((z) => z.subId === targetSubZone.subId);
    const others = newState.sampleZones.filter((z) => z.subId !== targetSubZone.subId);
    const count = currentPoints.length;
    const newPoints = await createPointGrid(targetSubZone, count, 0, 400, cellSideAttempts);
    if (newPoints.length < count) {
      console.warn('⚠ Unable to place all the points, leaving the old ones.');
      return;
    }

    const renamed = newPoints.map((p, i) => {
      const old = currentPoints[i];
      const name = old?.sampleName || `S${i + 1}`;
      return {
        ...p,
        sampleName: name,
        properties: { ...p.properties, sampleName: name },
        id: old?.id || p.id,
      };
    });

    const updated = [...others, ...renamed];
    setNewState((prev) => ({ ...prev, sampleZones: updated }));

    if (tools?.draw?.deleteAll) tools.draw.deleteAll();
    if (tools?.draw?.add) tools.draw.add(featureCollection([targetSubZone.polygon[0], ...renamed]));
  };
  useEffect(() => {
    if (!tools) return;
    try {
      withLoader(drawDefaultDataSampleZones);
    } catch (e) {
      console.log(e);
    }
  }, [tools]);

  useEffect(() => {
    if (!tools) return;
    switchMapEventListeners('on');
    return () => switchMapEventListeners('off');
  }, [tools, newState]);
  return (
    <div className="map">
      {isLoading && <div className="cropper__loader"><Loader /></div>}
      <div className="map__box">
        <MapHeader coordinates={centerCoordinates} handleClose={() => setShow(false)} />
        <div ref={mapRef} className="map__map" />
        <MapInfo
          data={{ numberSampleZones, sampleZonesLimit }}
          extra={{ warning }}
          handleClick={() => scaleBeforeSave()}
          shuffle={() => withLoader(shufflePoints)}
          viewType="sample-map"
        />
        {showModal && (
        <RequestModal
          error={showModal}
          handleClose={() => {
            window.location.reload();
            setShowModal(false);
          }}
          click={() => {
            window.location.reload();
            setShowModal(false);
          }}
          buttonText={translate.formatMessage({ id: 'modal.button.plots' })}
          description={(
            <FormattedMessage
              id="modal.description.plots"
              values={{
                link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
              }}
            />
          )}
        />
        )}
      </div>
    </div>
  );
};

export default SamplePlotsMap;
